import { useEffect, useRef, useState } from "react";
import type { ClockInInputDto } from "@carry/contract";
import {
  Button,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@carry/ui";

/** 出勤打刻時に同時取得する点呼 (アルコール検査) 結果。F16 で打刻前工程として必須化。 */
export interface ClockInAlcohol {
  result: "pass" | "fail";
  /** 測定値 (mg/L)。未入力時は undefined。 */
  measured_value?: number;
}

export type ClockInWeather = NonNullable<ClockInInputDto["weather"]>;

const WEATHER_OPTIONS: Array<{ value: ClockInWeather; label: string }> = [
  { value: "sunny", label: "晴" },
  { value: "cloudy", label: "曇" },
  { value: "rainy", label: "雨" },
  { value: "snowy", label: "雪" },
  { value: "other", label: "その他" },
];

export function OdometerDialog({
  title,
  description,
  confirmLabel,
  destructive = false,
  withAlcohol = false,
  withWeather = false,
  defaultValue,
  onCancel,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  /** true のとき点呼 (アルコール検査) 入力を表示し、結果を onConfirm に渡す (出勤打刻用)。 */
  withAlcohol?: boolean;
  /** true のとき天気の必須選択を表示し、結果を onConfirm に渡す (出勤打刻用)。 */
  withWeather?: boolean;
  /** オドメーター入力の初期値 (km)。修正時に登録済み値をプリフィルする用途。未指定は空欄。 */
  defaultValue?: number;
  onCancel: () => void;
  onConfirm: (
    odometer?: number,
    alcohol?: ClockInAlcohol,
    weather?: ClockInWeather,
  ) => void | Promise<void>;
}) {
  const [raw, setRaw] = useState(defaultValue != null ? String(defaultValue) : "");
  const [alcoholRaw, setAlcoholRaw] = useState("0.00");
  const [alcoholResult, setAlcoholResult] = useState<"pass" | "fail">("pass");
  // onConfirm (API 通信を含む) の実行中は送信中表示にし、二重送信・誤操作クローズを防ぐ。
  const [submitting, setSubmitting] = useState(false);
  const [weather, setWeather] = useState<ClockInWeather | "">("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, submitting]);
  const submit = async () => {
    if (submitting) return;
    if (withWeather && !weather) return;
    const n = raw ? parseInt(raw.replace(/[^\d]/g, ""), 10) : NaN;
    const odo = Number.isFinite(n) && n > 0 ? n : undefined;
    setSubmitting(true);
    try {
      if (!withAlcohol) {
        if (withWeather) {
          await onConfirm(odo, undefined, weather || undefined);
          return;
        }
        await onConfirm(odo);
        return;
      }
      const mv = Number(alcoholRaw);
      const alcohol: ClockInAlcohol = {
        result: alcoholResult,
        measured_value: Number.isFinite(mv) ? mv : undefined,
      };
      if (withWeather) {
        await onConfirm(odo, alcohol, weather || undefined);
        return;
      }
      await onConfirm(odo, alcohol);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="odo-title"
      onClick={() => {
        if (!submitting) onCancel();
      }}
      className="safe-bottom fixed inset-0 z-[200] flex items-end justify-center bg-black/40 px-4 pt-4"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="max-h-[90dvh] w-full max-w-[420px] overflow-y-auto rounded-2xl bg-[var(--bg-raised)] p-5 font-[family-name:var(--f-sans)] shadow-[0_-10px_30px_rgba(0,0,0,0.2)]"
      >
        <div id="odo-title" className="mb-1.5 text-[length:var(--fs-lg)] font-semibold">
          {title}
        </div>
        <div className="mb-3.5 text-[length:var(--fs-xs)] leading-relaxed text-[var(--ink-3)]">
          {description}
        </div>
        {withWeather && (
          <div className="mb-3.5">
            <label
              htmlFor="weather-select"
              className="mb-1.5 block text-[length:var(--fs-xs)] text-[var(--ink-3)]"
            >
              天気 (必須)
            </label>
            <Select
              value={weather || undefined}
              onValueChange={(v) => {
                if (v) setWeather(v as ClockInWeather);
              }}
            >
              <SelectTrigger
                id="weather-select"
                aria-label="天気 (必須)"
                className="h-12 w-full text-base"
              >
                <SelectValue>
                  {(val: string | null) =>
                    WEATHER_OPTIONS.find((o) => o.value === val)?.label ?? "選択してください"
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {WEATHER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <label
          htmlFor="odo-input"
          className="mb-1.5 block text-[length:var(--fs-xs)] text-[var(--ink-3)]"
        >
          オドメーター (km)
        </label>
        <Input
          id="odo-input"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="例: 123456"
          className="h-12 text-base"
        />
        {withAlcohol && (
          <div className="mt-3.5">
            <label
              htmlFor="alcohol-input"
              className="mb-1.5 block text-[length:var(--fs-xs)] text-[var(--ink-3)]"
            >
              アルコール検査 (出勤前点呼・必須)
            </label>
            <Input
              id="alcohol-input"
              type="number"
              step="0.01"
              min="0"
              max="9.99"
              value={alcoholRaw}
              onChange={(e) => {
                setAlcoholRaw(e.target.value);
                const v = Number(e.target.value);
                setAlcoholResult(Number.isFinite(v) && v > 0 ? "fail" : "pass");
              }}
              placeholder="測定値 (mg/L)"
              className="num h-12 text-center text-base"
            />
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => setAlcoholResult("pass")}
                className={cn(
                  "flex-1 cursor-pointer rounded-lg py-2.5 text-[length:var(--fs-sm)] font-semibold",
                  alcoholResult === "pass"
                    ? "border-2 border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--st-done-ink)]"
                    : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
                )}
              >
                PASS
              </button>
              <button
                type="button"
                onClick={() => setAlcoholResult("fail")}
                className={cn(
                  "flex-1 cursor-pointer rounded-lg py-2.5 text-[length:var(--fs-sm)] font-semibold",
                  alcoholResult === "fail"
                    ? "border-2 border-[var(--danger)] bg-[var(--st-cancel-bg)] text-[var(--danger)]"
                    : "border border-[var(--rule-soft)] bg-[var(--bg-raised)] text-[var(--ink-3)]",
                )}
              >
                FAIL
              </button>
            </div>
            {alcoholResult === "fail" && (
              <div className="mt-2 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
                ⚠ 不合格のため出勤打刻はできません
              </div>
            )}
          </div>
        )}
        <div className="mt-3.5 flex gap-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            className="h-12 flex-1 text-[length:var(--fs-sm)] font-semibold"
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            variant={destructive ? "destructive" : "default"}
            disabled={submitting || (withWeather && !weather)}
            className={cn(
              "h-12 flex-[2] text-[length:var(--fs-sm)] font-bold",
              !destructive && "bg-[var(--ok)] text-white hover:bg-[var(--ok)]/90",
              submitting && "cursor-wait opacity-70",
            )}
          >
            {submitting ? "送信中…" : confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}
